/**
 * MicroscopeEngine — adaptive pitch-preserving time-stretch router for
 * /audio-microscope. Picks the RIGHT engine for the slowdown factor:
 *
 *   factor ≤ ~7  -> Signalsmith Stretch (vendored WASM, transient-preserving,
 *                   keeps speech/music recognizable — "what is IN this sound").
 *   factor ≥ ~8  -> Paulstretch (paulstretch-worker.js, smooth spectral freeze —
 *                   the only thing that sounds intentional at 8x–500x).
 *
 * Verified on real files (snap/drum/harmonic/speech, 2x–500x): a single coherent
 * vocoder is structurally wrong across this whole range (above ~4x the old naive
 * PV's synthesis hop exceeded its window → isolated grains + silence; even
 * Signalsmith goes patchy past ~100x). Routing by factor fixes the ROOT cause.
 *
 * The user can override the crossover with a "mode": auto | detail | texture.
 *
 * API:
 *   await MicroscopeEngine.stretch(audioBuffer, {factor, mode, onProgress}) -> AudioBuffer
 *   MicroscopeEngine.engineFor(factor, mode) -> 'signalsmith' | 'paulstretch'
 *   MicroscopeEngine.preload()  MicroscopeEngine.terminate()
 */
(function () {
'use strict';

var PAUL_WORKER_URL = (typeof window!=='undefined'&&window.__jsSrc?window.__jsSrc('/js/paulstretch-worker.js'):'/js/paulstretch-worker.js');
var _paul = null, _paulId = 0, _paulPending = new Map();

function paulWorker() {
    if (_paul) return _paul;
    // cache-bust per deploy (the blade sets window.__MIC_WORKER_V to the file mtime)
    var url = PAUL_WORKER_URL + (window.__MIC_WORKER_V ? '?v=' + window.__MIC_WORKER_V : '');
    _paul = new Worker(url);
    _paul.onmessage = function (ev) {
        var m = ev.data, p = _paulPending.get(m.id);
        if (!p) return;
        if (m.type === 'progress') { if (p.onProgress) p.onProgress(m.pct); return; }
        if (m.type === 'done') { _paulPending.delete(m.id); p.resolve(m.channels); return; }
        if (m.type === 'error') { _paulPending.delete(m.id); p.reject(new Error(m.error || 'paulstretch error')); return; }
    };
    _paul.onerror = function (e) {
        _paulPending.forEach(function (p) { p.reject(new Error('paulstretch worker crashed: ' + (e.message || e))); });
        _paulPending.clear();
        try { _paul.terminate(); } catch (_) {}
        _paul = null;
    };
    return _paul;
}

function safeBuffer(ch, length, sr) {
    if (typeof window.__safeAudioBuffer === 'function') {
        return window.__safeAudioBuffer({ numberOfChannels: ch, length: length, sampleRate: sr });
    }
    var Ctx = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    return new Ctx(ch, length, sr).createBuffer(ch, length, sr);
}

function winSecFor(factor) {
    if (factor < 30) return 0.25;
    if (factor < 100) return 0.45;
    return 1.0; // 100x–500x: smoothest freeze + finest freq resolution
}

function engineFor(factor, mode) {
    if (factor <= 1.001) return 'signalsmith'; // ~no-op, faithful
    if (mode === 'detail') return factor <= 25 ? 'signalsmith' : 'paulstretch';
    if (mode === 'texture') return factor <= 3 ? 'signalsmith' : 'paulstretch';
    return factor <= 7 ? 'signalsmith' : 'paulstretch'; // auto
}

function signalsmithAvailable() {
    return !!(window.SignalsmithWorkerEngine && typeof window.SignalsmithWorkerEngine.shift === 'function');
}

function paulstretch(audioBuffer, factor, onProgress, winSecOverride) {
    var ch = audioBuffer.numberOfChannels, sr = audioBuffer.sampleRate;
    var channels = [], transfers = [];
    for (var c = 0; c < ch; c++) {
        var copy = new Float32Array(audioBuffer.getChannelData(c));
        channels.push(copy); transfers.push(copy.buffer);
    }
    var winSec = winSecOverride || winSecFor(factor);
    return new Promise(function (resolve, reject) {
        var id = ++_paulId;
        _paulPending.set(id, { resolve: resolve, reject: reject, onProgress: onProgress });
        var w = paulWorker();
        // seed varies per call by id so successive runs aren't bit-identical noise
        w.postMessage({ id: id, type: 'process', channels: channels, sampleRate: sr,
            factor: factor, winSec: winSec, seed: (id * 2654435761) >>> 0 }, transfers);
    }).then(function (outChannels) {
        var len = (outChannels[0] && outChannels[0].length) || 1;
        var out = safeBuffer(ch, Math.max(1, len), sr);
        for (var c2 = 0; c2 < ch; c2++) {
            if (outChannels[c2] && outChannels[c2].length) out.getChannelData(c2).set(outChannels[c2]);
        }
        return out;
    });
}

async function stretch(audioBuffer, opts) {
    opts = opts || {};
    var factor = Number(opts.factor) || 1;
    var mode = opts.mode || 'auto';
    var onProgress = typeof opts.onProgress === 'function' ? opts.onProgress : null;
    var eng = engineFor(factor, mode);

    if (eng === 'signalsmith') {
        if (signalsmithAvailable()) {
            try {
                var buf = await window.SignalsmithWorkerEngine.shift(audioBuffer,
                    { rate: 1 / factor, semitones: 0, onProgress: onProgress });
                return { buffer: buf, engine: 'signalsmith' };
            } catch (e) {
                // fall through to Paulstretch fallback (pure-JS, no WASM dependency)
            }
        }
        // Signalsmith unavailable/failed: low-factor Paulstretch with a small window
        // is the least-mushy fallback (keeps more event structure than 0.25s).
        var fb = await paulstretch(audioBuffer, factor, onProgress, 0.10);
        return { buffer: fb, engine: 'paulstretch-fallback' };
    }

    var ps = await paulstretch(audioBuffer, factor, onProgress);
    return { buffer: ps, engine: 'paulstretch' };
}

function preload() {
    try { if (signalsmithAvailable()) window.SignalsmithWorkerEngine.preload(); } catch (_) {}
    try { paulWorker().postMessage({ id: 0, type: 'noop' }); } catch (_) {}
}

function terminate() {
    if (_paul) { try { _paul.terminate(); } catch (_) {} _paul = null; }
    _paulPending.forEach(function (p) { p.reject(new Error('terminated')); });
    _paulPending.clear();
    try { if (signalsmithAvailable()) window.SignalsmithWorkerEngine.terminate(); } catch (_) {}
}

window.MicroscopeEngine = {
    stretch: stretch,
    engineFor: engineFor,
    winSecFor: winSecFor,
    preload: preload,
    terminate: terminate,
    version: '1.0.0'
};

})();
